import React, { useEffect, useState, Component } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps } from 'recharts';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, DollarSign, ArrowUpRight } from 'lucide-react';
// --- Types ---
interface ChartData {
  quarter: string;
  year2024: number;
  year2025: number;
}
// --- Data ---
const chartData: ChartData[] = [{
  quarter: 'Q1',
  year2024: 80000000,
  year2025: 95000000
}, {
  quarter: 'Q2',
  year2024: 175000000,
  year2025: 185000000
}, {
  quarter: 'Q3',
  year2024: 135000000,
  year2025: 215000000
}, {
  quarter: 'Q4',
  year2024: 190000000,
  year2025: 210000000
}];
const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${value / 1000000}M`;
  }
  return `$${value}`;
};
const CustomTooltip = ({
  active,
  payload,
  label
}: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return <div className="bg-[#1A1D24] border border-[#2A2E37] p-4 rounded-sm shadow-xl" data-id="element-1">
        <p className="text-[#F5F0E8] font-bold mb-2" data-id="element-2">{label}</p>
        <div className="space-y-1" data-id="element-3">
          <p className="text-[#D4CFC7] text-sm" data-id="element-4">
            2024:{' '}
            <span className="font-mono" data-id="element-5">
              ${(payload[0].value as number).toLocaleString()}
            </span>
          </p>
          <p className="text-[#C4D62C] text-sm" data-id="element-6">
            2025:{' '}
            <span className="font-mono" data-id="element-7">
              ${(payload[1].value as number).toLocaleString()}
            </span>
          </p>
        </div>
      </div>;
  }
  return null;
};
// --- Components ---
const StatCard = ({
  label,
  value,
  subtext,
  delay,
  icon: Icon
}: {
  label: string;
  value: string | React.ReactNode;
  subtext?: string;
  delay: number;
  icon?: React.ElementType;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} animate={{
  opacity: 1,
  y: 0
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className="flex flex-col p-6 border-t border-[#2A2E37] relative group" data-id="element-8">
    <div className="absolute top-0 left-0 w-0 h-[1px] bg-[#C4D62C] group-hover:w-full transition-all duration-700 ease-in-out" data-id="element-9" />

    <div className="flex items-center justify-between mb-4" data-id="element-10">
      <span className="text-[#8A8F98] text-sm uppercase tracking-wider font-medium" data-id="element-11">
        {label}
      </span>
      {Icon && <Icon className="w-5 h-5 text-[#C4D62C] opacity-80" data-id="element-12" />}
    </div>

    <div className="mt-auto" data-id="element-13">
      <div className="text-[#F5F0E8] text-4xl lg:text-5xl font-bold tracking-tight mb-2 font-['Inter']" data-id="element-14">
        {value}
      </div>
      {subtext && <p className="text-[#8A8F98] text-sm leading-relaxed max-w-xs" data-id="element-15">
          {subtext}
        </p>}
    </div>
  </motion.div>;
export function CityFringeDashboard() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <div className="min-h-screen w-full bg-[#0D0F14] text-[#F5F0E8] font-['Inter'] overflow-x-hidden selection:bg-[#C4D62C] selection:text-[#0D0F14]" data-id="element-16">
      <style data-id="element-17">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          .grid-pattern {
            background-image: linear-gradient(to right, #1A1D24 1px, transparent 1px),
                              linear-gradient(to bottom, #1A1D24 1px, transparent 1px);
            background-size: 40px 40px;
            mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
          }
        `}
      </style>

      {/* Background Texture */}
      <div className="fixed inset-0 grid-pattern opacity-20 pointer-events-none" data-id="element-18" />

      <div className="max-w-[1440px] mx-auto px-6 py-12 relative z-10" data-id="element-19">
        {/* Header */}
        <motion.header initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="mb-16 border-b border-[#2A2E37] pb-8 flex flex-col md:flex-row md:items-end justify-between gap-6" data-id="element-20">
          <div data-id="element-21">
            <div className="flex items-center gap-3 mb-2" data-id="element-22">
              <span className="inline-block w-2 h-2 bg-[#C4D62C] rounded-full animate-pulse" data-id="element-23" />
              <span className="text-[#C4D62C] text-sm font-mono uppercase tracking-widest" data-id="element-24">
                Market Intelligence
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight tracking-tight text-white" data-id="element-25">
              Overview of City <br data-id="element-26" />
              <span className="text-[#8A8F98]" data-id="element-27">Fringe Deals</span> 2025
            </h1>
          </div>
          <div className="flex flex-col items-end gap-4" data-id="element-28">
            <img src="https://cdn.magicpatterns.com/uploads/8sKZ3cSsPAouSY1jdARtwc/Stonebridge_logo_master_reversed_1.0.png" alt="Stonebridge Logo" className="h-10 w-auto object-contain" data-id="element-29" />
            <div className="text-right hidden md:block" data-id="element-30">
              <p className="text-[#8A8F98] text-sm font-mono" data-id="element-31">
                REPORT GENERATED
              </p>
              <p className="text-[#F5F0E8] text-lg font-medium" data-id="element-32">FEB 19, 2026</p>
            </div>
          </div>
        </motion.header>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-33">
          <StatCard label="Total Sales" value="141" delay={0.2} icon={TrendingUp} subtext="Confirmed transactions across all sectors in the fringe region." data-id="element-34" />
          <StatCard label="Total Sale Volume" value={<span className="flex items-start" data-id="element-36">
                $688,511,130
                <span className="text-[#C4D62C] text-2xl ml-1" data-id="element-37">*</span>
              </span>} delay={0.4} icon={DollarSign} subtext="*Includes all recorded commercial and residential transactions YTD." data-id="element-35" />
          <StatCard label="Property Types" value={<span className="text-2xl lg:text-3xl leading-snug block pt-2" data-id="element-39">
                Office, Warehouse,
                <br data-id="element-40" />
                Retail & Mixed-Use
              </span>} delay={0.6} icon={Building2} subtext="Dominant asset classes by volume." data-id="element-38" />
        </div>

        {/* Chart Section */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="bg-[#13161C] border border-[#2A2E37] p-8 md:p-12 rounded-sm relative overflow-hidden" data-id="element-41">
          {/* Decorative corner accents */}
          <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-[#C4D62C]" data-id="element-42" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-[#2A2E37]" data-id="element-43" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-[#2A2E37]" data-id="element-44" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-[#C4D62C]" data-id="element-45" />

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10" data-id="element-46">
            <div data-id="element-47">
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-id="element-48">
                Transaction Volume Per Quarter
                <ArrowUpRight className="w-5 h-5 text-[#C4D62C]" data-id="element-49" />
              </h2>
              <p className="text-[#8A8F98]" data-id="element-50">
                Comparative analysis 2024 vs 2025 (AUD)
              </p>
            </div>

            {/* Custom Legend */}
            <div className="flex items-center gap-6 mt-4 md:mt-0 bg-[#0D0F14] px-4 py-2 rounded-full border border-[#2A2E37]" data-id="element-51">
              <div className="flex items-center gap-2" data-id="element-52">
                <div className="w-3 h-3 bg-[#D4CFC7] rounded-sm" data-id="element-53" />
                <span className="text-sm text-[#D4CFC7] font-medium" data-id="element-54">2024</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-55">
                <div className="w-3 h-3 bg-[#C4D62C] rounded-sm" data-id="element-56" />
                <span className="text-sm text-[#C4D62C] font-medium" data-id="element-57">2025</span>
              </div>
            </div>
          </div>

          <div className="h-[400px] w-full" data-id="element-58">
            <ResponsiveContainer width="100%" height="100%" data-id="element-59">
              <BarChart data={chartData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} barGap={6} barCategoryGap="20%" data-id="element-60">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#2A2E37" data-id="element-61" />
                <XAxis dataKey="quarter" axisLine={false} tickLine={false} tick={{
                fill: '#8A8F98',
                fontSize: 14,
                fontWeight: 500
              }} dy={10} data-id="element-62" />
                <YAxis axisLine={false} tickLine={false} tickFormatter={formatCurrency} tick={{
                fill: '#8A8F98',
                fontSize: 12
              }} dx={-10} data-id="element-63" />
                <Tooltip content={<CustomTooltip data-id="element-65" />} cursor={{
                fill: '#1A1D24',
                opacity: 0.5
              }} data-id="element-64" />
                <Bar dataKey="year2024" fill="#D4CFC7" radius={[2, 2, 0, 0]} animationDuration={1500} animationBegin={800} data-id="element-66" />
                <Bar dataKey="year2025" fill="#C4D62C" radius={[2, 2, 0, 0]} animationDuration={1500} animationBegin={1000} data-id="element-67" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-[#2A2E37] flex flex-col md:flex-row justify-between items-center text-[#4B505C] text-sm" data-id="element-68">
          <p data-id="element-69">© 2026 City Fringe Analytics. All rights reserved.</p>
          <div className="flex gap-6 mt-4 md:mt-0" data-id="element-70">
            <span data-id="element-71">Data Source: Internal Records</span>
            <span data-id="element-72">Privacy Policy</span>
          </div>
        </footer>
      </div>
    </div>;
}